/**
 * Utility functions for handling embedded iframe context
 */

/**
 * Check if we're currently in an embedded context (iframe)
 */
export function isEmbeddedContext(): boolean {
  // Check if running in iframe
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we can't access parent, we're likely in iframe with different origin
    return true;
  }
}

/**
 * Check if current route is an embedded route
 */
export function isEmbeddedRoute(path: string): boolean {
  return path.includes('/embed/');
}

/**
 * Handle payment success redirect for embedded context
 */
export function handlePaymentSuccessRedirect(amount: number, email?: string): void {
  const urlParams = new URLSearchParams(window.location.search);
  const isEmbedded = urlParams.get('embedded') === 'true' || isEmbeddedContext();

  if (isEmbedded) {
    // Try to get the referrer to redirect back to organization profile
    const { referrer } = document;

    if (referrer && (referrer.includes('/web/embed/org/') || referrer.includes('/web/embed/organization/'))) {
      // Redirect back to organization profile
      window.location.href = referrer;
    } else {
      // Post message to parent window to handle the success
      window.parent.postMessage({
        type: 'payment_success',
        amount,
        email,
      }, '*');

      // Close the payment window after a short delay
      setTimeout(() => {
        window.close();
      }, 2000);
    }
  } else {
    // Normal non-embedded flow
    const redirectUrl = window.location.origin;
    const store = (window as any).$store; // Access Vuex store if available

    if (store?.getters['users/me']) {
      const baseUrl = `${redirectUrl}/web/stickers/purchased`;
      const queryParams = `action=purchaseSticker&amount=${amount}&success=true`;
      window.location.href = `${baseUrl}?${queryParams}`;
    } else if (email) {
      const encodedEmail = encodeURIComponent(email);
      const profileUrl = `${redirectUrl}/auth/web/complete-profile?email=${encodedEmail}`;
      // Preserve embedded context when redirecting
      window.location.href = addEmbeddedContextToUrl(profileUrl);
    } else {
      window.location.href = `${redirectUrl}/web/`;
    }
  }
}

/**
 * Add embedded context to payment URL
 */
export function addEmbeddedContextToUrl(url: string): string {
  const urlObj = new URL(url, window.location.origin);
  const params = new URLSearchParams(urlObj.search);

  // Check if we're in embedded context
  if (isEmbeddedRoute(window.location.pathname) || isEmbeddedContext()) {
    params.set('embedded', 'true');

    // Add referrer information for proper redirect
    if (document.referrer) {
      params.set('referrer', encodeURIComponent(document.referrer));
    }

    // Extract and preserve embed parameters from current URL
    const currentUrl = new URL(window.location.href);
    const currentParams = new URLSearchParams(currentUrl.search);

    // Preserve embedOrganizationId if present
    if (currentParams.has('embedOrganizationId')) {
      const embedOrgId = currentParams.get('embedOrganizationId');
      if (embedOrgId) {
        params.set('embedOrganizationId', embedOrgId);
      }
    } else {
      // Try to extract from current path if it's an organization embed
      const embedOrgMatch = window.location.pathname.match(/\/embed\/organization\/([^/?]+)/);
      if (embedOrgMatch) {
        params.set('embedOrganizationId', embedOrgMatch[1]);
      }
    }

    // Preserve embedSource (embedHost) if present
    if (currentParams.has('embedSource')) {
      const embedSource = currentParams.get('embedSource');
      if (embedSource) {
        params.set('embedSource', embedSource);
      }
    }
  }

  urlObj.search = params.toString();
  return urlObj.toString();
}
